// Migration helpers for transitioning from hardcoded theme values to centralized tokens
// These utilities help maintain the current design while preparing for Material Design 3

import { 
  isDarkMode, 
  getThemeColors, 
  getTranslucentColor, 
  getBorderColor,
  getBackgroundColor,
  getTextColor,
  getHoverColor,
  getElevation
} from './index';

// Legacy compatibility layer - provides the same values as before but from centralized theme
export const getLegacyColors = (theme) => {
  const isDark = isDarkMode(theme);
  const colors = getThemeColors(theme);
  
  return {
    // Background helpers
    sidebarBackground: isDark ? colors.background.sidebar : colors.background.sidebar,
    appBarBackground: isDark ? colors.background.appBar : colors.background.appBar,
    messageBackground: isDark ? colors.background.message.user : colors.background.message.user,
    
    // Border helpers  
    defaultBorder: getBorderColor(theme),
    focusBorder: colors.border?.focus || theme.palette.primary.main,
    
    // Text helpers
    primaryText: getTextColor(theme, 'primary'),
    secondaryText: getTextColor(theme, 'secondary'),
    groupHeaderText: getTextColor(theme, 'groupHeader'),
    menuIconText: getTextColor(theme, 'menuIcon'),
    
    // Interactive state helpers
    hoverBackground: getHoverColor(theme),
    selectedBackground: theme.palette.action.selected,
    translucentBackground: getTranslucentColor(theme),
    
    // Shadows
    menuShadow: isDark ? getElevation(theme, 'xl') : getElevation(theme, 'lg'),
    buttonShadow: getElevation(theme, 'sm'),
  };
};

// Specific helpers for common patterns found in the codebase
export const createModeBasedStyle = (theme, lightValue, darkValue) => {
  return isDarkMode(theme) ? darkValue : lightValue;
};

// Helper for the common pattern: theme.palette.mode === 'dark' ? darkColor : lightColor
export const getModeBasedColor = (theme, lightColor, darkColor) => {
  return createModeBasedStyle(theme, lightColor, darkColor);
};

// Helper for translucent overlays with different opacities
export const getTranslucentOverlay = (theme, opacity = 0.05) => {
  const isDark = isDarkMode(theme);
  return isDark 
    ? `rgba(255, 255, 255, ${opacity})`
    : `rgba(0, 0, 0, ${opacity})`;
};

// Helper for border colors with custom alpha
export const getBorderWithAlpha = (theme, alpha = 0.12) => {
  const isDark = isDarkMode(theme);
  return isDark 
    ? `rgba(255, 255, 255, ${alpha})`
    : `rgba(0, 0, 0, ${alpha})`;
};

// Helper for selection/focus indicators
export const getSelectionIndicator = (theme, selected = false) => {
  const colors = getThemeColors(theme);
  const isDark = isDarkMode(theme);
  
  if (selected) {
    return {
      border: `2px solid ${isDark ? '#fff' : '#000'}`,
      boxShadow: `0 0 0 2px ${isDark ? '#fff' : '#000'}`,
    };
  }
  
  return {
    border: 'none',
    boxShadow: 'none',
  };
};

// Helper for component-specific background patterns
export const getComponentBackground = (theme, component, state = 'default') => {
  const colors = getThemeColors(theme);
  const componentColors = colors[component] || {};
  
  if (componentColors.background && componentColors.background[state]) {
    return componentColors.background[state];
  }
  
  // Fallback to general patterns
  switch (state) {
    case 'hover':
      return getHoverColor(theme);
    case 'selected':
      return theme.palette.action.selected;
    case 'disabled':
      return theme.palette.action.disabled;
    default:
      return 'transparent';
  }
};

// Utility to convert old style objects to new theme-aware ones
export const migrateStyleObject = (theme, styleObj) => {
  const result = { ...styleObj };
  const colors = getThemeColors(theme);
  
  // Replace common hardcoded patterns
  Object.keys(result).forEach(key => {
    const value = result[key];
    
    if (typeof value === 'string') {
      // Replace hardcoded mode checks in string values
      if (value.includes("theme.palette.mode === 'dark'")) {
        // This would need more sophisticated parsing in a real implementation
        // For now, we'll handle this case-by-case
      }
    }
    
    if (typeof value === 'object' && value !== null) {
      result[key] = migrateStyleObject(theme, value);
    }
  });
  
  return result;
};

// Helpers for specific component migrations
export const getChatSessionItemStyles = (theme, isActive = false, isHovered = false) => {
  const colors = getThemeColors(theme);
  const chatColors = colors.chatSession || {};
  
  let backgroundColor = 'transparent';
  let textColor = chatColors.text?.default || theme.palette.text.primary;
  
  if (isActive && isHovered) {
    backgroundColor = chatColors.background?.activeHover || getTranslucentColor(theme, 'xl');
    textColor = chatColors.text?.active || theme.palette.primary.main;
  } else if (isActive) {
    backgroundColor = chatColors.background?.active || getTranslucentColor(theme, 'lg');
    textColor = chatColors.text?.active || theme.palette.primary.main;
  } else if (isHovered) {
    backgroundColor = chatColors.background?.hover || getTranslucentColor(theme, 'md');
  }
  
  return {
    backgroundColor,
    color: textColor,
    transition: 'background-color 0.2s ease',
  };
};

export const getMenuButtonStyles = (theme, disabled = false) => {
  const colors = getThemeColors(theme);
  const menuColors = colors.action?.menuButton || {};
  
  if (disabled) {
    return {
      backgroundColor: getTranslucentColor(theme, 'base'),
      borderColor: getBorderColor(theme),
      color: theme.palette.text.disabled,
    };
  }
  
  return {
    backgroundColor: menuColors.background || getTranslucentColor(theme, 'base'),
    borderColor: menuColors.border || theme.palette.primary.main,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: menuColors.hover || getTranslucentColor(theme, 'md'),
      borderColor: theme.palette.primary.main,
      boxShadow: getElevation(theme, isDarkMode(theme) ? 'md' : 'base'),
      transform: 'translateY(-1px)',
    },
  };
};

export const getProjectItemStyles = (theme, disabled = false) => {
  const colors = getThemeColors(theme);
  const projectColors = colors.action?.projectItem || {};
  
  return {
    backgroundColor: projectColors.background || getTranslucentColor(theme, 'xs'),
    border: `1px solid ${projectColors.border || getBorderColor(theme)}`,
    '&:hover': disabled ? {} : {
      backgroundColor: projectColors.hover || getTranslucentColor(theme, 'base'),
    },
  };
}; 